import React, {
  useState, useEffect, useContext,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import ReactMarkdown from 'react-markdown';
import format from 'string-template';
import { CardContent, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import FirebaseContext from '../../../context/FirebaseContext';
import CoachIdentity, { CoachIdentityStatus } from '../../../Model/CoachIdentity';
import InstagramUser from '../../../Model/InstagramUser';
import ColoredHeader from '../../components/ColoredHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
import useToast from '../../hooks/useToast';
import CoachSelect from '../../components/Leads/components/CoachSelect';
import {
  Title,
  OptionContainer,
  Container,
  CardTitle,
  StyledEditButton,
  CardHeader,
  CardContainer,
  SubTitle,
  GenerateButton,
  ButtonContainer,
  StyledRegenerateButton,
} from '../AICoachProfile/styles';
import EditCoachProfileModal from '../AICoachProfile/EditCoachProfileModal';
import texts from './texts.json';

const AICoachInstagramIdentity = ({ role }) => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const [selectedCoach, setSelectedCoach] = useState({});
  const [profileText, setProfileText] = useState('');
  const [instagramUserDoc, setInstagramUserDoc] = useState(null);
  const [isEditCoachProfile, setIsEditCoachProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const fetchIdentity = async (coachId) => {
    const coachIdentity = await CoachIdentity.getCoachIdentity(coachId);
    if (!coachIdentity) {
      return '';
    }
    return coachIdentity.coachIdentityPrompt;
  };

  /**
   * Fetch the coach identity for the selected coach
   * @param {string} coachId - The ID of the coach
   * @returns {Promise<string>} The coach identity
   */
  useEffect(() => {
    if (!selectedCoach.id) {
      return;
    }
    setIsLoading(true);
    const doFetch = async () => {
      try {
        const identityMarkdown = await fetchIdentity(selectedCoach.id);
        setProfileText(identityMarkdown);
        const coachInstagramUserCollection = await InstagramUser.getInstaUserByCoachId(selectedCoach.id);
        const coachInstagramUserDoc = coachInstagramUserCollection?.hasDocs
          ? coachInstagramUserCollection.docs[0] : undefined;
        setInstagramUserDoc(coachInstagramUserDoc);
        if (!coachInstagramUserDoc) {
          const defaultCoachIdentity = await CoachIdentity.getDefaultCoachIdentity();
          setProfileText(defaultCoachIdentity?.coachIdentityPrompt || '');
        }
      } catch (error) {
        const errorMessage = `${texts.fetchError}: ${error.message}`;
        Sentry.captureException(new Error(errorMessage), {
          extra: {
            coachId: selectedCoach.id,
            functionName: 'fetchIdentity',
            error: JSON.stringify(error, null, 2),
          },
        });
        showToast(errorMessage, { error: true });
      }
      setIsLoading(false);
    };

    doFetch();
  }, [selectedCoach.id, showToast]);

  /**
   * Generate the coach identity for the selected coach
   * @returns {Promise<void>}
   */
  const handleProfileGeneration = async () => {
    setIsLoading(true);
    try {
      const response = await remote(
        'coachProfile-generateInstagramAnalysis',
        { instagramUserId: instagramUserDoc.id },
        { method: 'POST' },
      );
      const { data, error } = await response.json();
      if (!response.ok) {
        throw new Error(error || 'Failed to generate');
      }
      setProfileText(data.coachIdentityPrompt || '');
    } catch (error) {
      const errorMessage = `${texts.generationError}: ${error.message}`;
      Sentry.captureException(new Error(errorMessage), {
        extra: {
          coachId: selectedCoach.id,
          functionName: 'handleProfileGeneration',
          error: JSON.stringify(error, null, 2),
        },
      });
      showToast(errorMessage, { error: true });
    }
    setIsLoading(false);
  };

  /**
   * Update the coach identity for the selected coach
   * @param {string} newProfileText - The new coach identity
   * @returns {Promise<void>}
   */
  const handleProfileUpdate = async (newProfileText) => {
    setIsLoading(true);
    try {
      const coachIdentity = new CoachIdentity(selectedCoach.id);
      await coachIdentity.updateFields({
        coachIdentityPrompt: newProfileText,
        coachId: selectedCoach.id,
        status: CoachIdentityStatus.ACTIVE,
        coachName: selectedCoach.label,
      });
      setProfileText(newProfileText);
      showToast(texts.profileUpdated, { success: true });
    } catch (error) {
      const errorMessage = `${texts.updateError}: ${error.message}`;
      Sentry.captureException(new Error(errorMessage), {
        extra: {
          coachId: selectedCoach.id,
          functionName: 'handleProfileUpdate',
          error: JSON.stringify(error, null, 2),
        },
      });
      showToast(errorMessage, { error: true });
    }
    setIsLoading(false);
    setIsEditCoachProfile(false);
  };

  let content;

  if (!selectedCoach.id && !isLoading) {
    content = <SubTitle>{texts.noCoachSelected}</SubTitle>;
  } else if (!profileText) {
    content = (
      <CardContainer>
        <SubTitle>{texts.noProfileText}</SubTitle>
        <GenerateButton variant="primary" fullWidth onClick={handleProfileGeneration}>
          <AutoAwesomeIcon />
          {texts.generateProfile}
        </GenerateButton>
      </CardContainer>
    );
  } else {
    content = (
      <CardContainer>
        <CardContent>
          <CardHeader>
            <CardTitle>{format(texts.profileTitle, { name: selectedCoach.label })}</CardTitle>
            <ButtonContainer>
              {instagramUserDoc && (
                <Tooltip title={texts.reGenerateProfile}>
                  <StyledRegenerateButton onClick={handleProfileGeneration} />
                </Tooltip>
              )}
              <StyledEditButton onClick={() => setIsEditCoachProfile(true)} />
            </ButtonContainer>
          </CardHeader>
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => (
                <p
                  style={{ whiteSpace: 'pre-line' }}
                  {...props}
                />
              ),
            }}
          >
            {profileText}
          </ReactMarkdown>
        </CardContent>
      </CardContainer>
    );
  }

  return (
    <Container>
      <ColoredHeader>
        <Title>{texts.title}</Title>
        <OptionContainer>
          <CoachSelect role={role} onChange={(coach) => setSelectedCoach(coach || {})} />
        </OptionContainer>
      </ColoredHeader>
      {isLoading ? null : (
        <>
          {content}
          <EditCoachProfileModal
            isOpen={isEditCoachProfile}
            onDidDismiss={() => setIsEditCoachProfile(false)}
            profileMarkdown={profileText}
            onUpdateProfile={handleProfileUpdate}
          />
        </>
      )}
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

AICoachInstagramIdentity.propTypes = {
  role: PropTypes.string,
};

AICoachInstagramIdentity.defaultProps = {
  role: '',
};

export default AICoachInstagramIdentity;
